import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowUpRight, AlertTriangle, Search, ShieldCheck, Circle } from "lucide-react";
import { TID } from "@/lib/testIds";
import { flagFor } from "@/lib/flags";
import InrAmount from "@/components/InrAmount";

/* Normalize a processing_stat like "3 weeks", "1-3 months", "15 days"
   into a lower-bound in DAYS so sorting reflects actual duration. */
function processingDays(s) {
  if (!s) return Infinity;
  const str = String(s).toLowerCase();
  // grab the FIRST number (lower bound of any range)
  const m = str.replace(/,/g, "").match(/-?\d+(?:\.\d+)?/);
  if (!m) return Infinity;
  const n = parseFloat(m[0]);
  if (/month/.test(str)) return n * 30;
  if (/week/.test(str))  return n * 7;
  return n; // days (default)
}

/* Extract the leading numeric value for the FEE sort. */
function firstNumber(s) {
  if (!s) return Infinity;
  const m = String(s).replace(/,/g, "").match(/-?\d+(?:\.\d+)?/);
  return m ? parseFloat(m[0]) : Infinity;
}

const SORTS = {
  alpha:      { label: "Alphabetical",     fn: (a, b) => a.country_name.localeCompare(b.country_name) },
  processing: { label: "Fastest processing", fn: (a, b) => processingDays(a.processing_stat) - processingDays(b.processing_stat) },
  fee:        { label: "Lowest fee",       fn: (a, b) => firstNumber(a.fee_stat) - firstNumber(b.fee_stat) },
};

function ConfidenceBadge({ badge, verified }) {
  const isVerified = badge === "verified";
  return (
    <span
      data-testid={`confidence-${isVerified ? "verified" : "estimate"}`}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] border ${
        isVerified
          ? "bg-[color:var(--moe-mint)] text-ink border-ink"
          : "bg-[color:var(--moe-yolk)] text-ink border-ink"
      }`}
    >
      {isVerified ? <ShieldCheck size={11} strokeWidth={2.4} /> : <Circle size={9} strokeWidth={3} fill="currentColor" />}
      {isVerified ? `Verified · ${verified || ""}` : "Estimate. Confirming with consulate"}
    </span>
  );
}

function ExceptionBadge({ label }) {
  if (!label) return null;
  return (
    <div
      data-testid="exception-badge"
      className="mt-5 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold border border-ink"
      style={{ background: "var(--moe-yolk)", color: "var(--moe-ink)" }}
    >
      <AlertTriangle size={12} strokeWidth={2.4} /> {label}
    </div>
  );
}

/* --- One country card. Every field is a scannable stat, hierarchy is
       Processing > Fee & Funds. Long context moves to a footnote. --- */
function CountryCard({ c, hasPlaybook }) {
  return (
    <Link
      to={`/countries/${encodeURIComponent(c.country_name)}`}
      data-testid={TID.countryCard(c.country_name)}
      className="moe-card p-6 moe-hover-lift block group"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span aria-hidden="true" className="text-2xl leading-none" data-testid={`flag-${c.country_name}`}>
              {flagFor(c.country_name)}
            </span>
            <div className="text-2xl font-bold text-ink tracking-tight truncate">{c.country_name}</div>
          </div>
          <div className="text-[11px] uppercase tracking-widest text-soft font-bold mt-1">{c.currency?.split(" ")[0]}</div>
        </div>
        <ArrowUpRight size={18} className="text-ink/60 group-hover:text-elec transition-colors shrink-0" />
      </div>

      {!hasPlaybook && (
        <div
          className="mt-4 inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] border border-ink/30 bg-tint text-ink"
          data-testid={`playbook-progress-${c.country_name}`}
        >
          <Circle size={8} strokeWidth={3} fill="currentColor" /> Playbook in progress
        </div>
      )}

      <div className="mt-6">
        {/* Processing time. The dominant figure. */}
        <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">Processing</div>
        <div className="mt-1 text-[32px] md:text-4xl font-bold tracking-[-0.03em] leading-[0.95] text-ink break-words">
          {c.processing_stat || "–"}
        </div>
        {c.processing_note && (
          <div className="mt-1 text-[11px] text-muted">{c.processing_note}</div>
        )}
      </div>

      {/* Fee + Funds. Secondary. */}
      <div className="mt-5 grid grid-cols-2 gap-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">Fee</div>
          <div className="mt-1 text-[15px] font-bold text-ink break-words">{c.fee_stat || "–"}</div>
          <InrAmount raw={c.fee_stat} size="xs" showRateLine={false} />
          {c.fee_note && <div className="mt-1 text-[11px] text-muted leading-snug">{c.fee_note}</div>}
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">Funds</div>
          <div className="mt-1 text-[15px] font-bold text-ink break-words">{c.funds_stat || "–"}</div>
          <InrAmount raw={c.funds_stat} size="xs" showRateLine={false} />
          {c.funds_note && <div className="mt-1 text-[11px] text-muted leading-snug">{c.funds_note}</div>}
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-ink/10 flex items-center justify-between gap-2">
        <ConfidenceBadge badge={c.confidence_badge} verified={c.confidence_verified_at} />
      </div>

      {c.exception_label && <ExceptionBadge label={c.exception_label} />}
    </Link>
  );
}

export default function Countries() {
  const [countries, setCountries] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [coverage, setCoverage] = useState({ with_playbook: [] });
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("alpha");

  useEffect(() => {
    api.get("/countries")
      .then((r) => {
        setCountries(r.data.countries);
        setLoadError(false);
      })
      .catch(() => {
        // Distinguish a fetch failure (network/CORS/backend down) from a
        // genuine empty result set — the old behaviour of falling into the
        // "No country matches ''" empty-state was misleading and hid outages.
        setCountries([]);
        setLoadError(true);
      });
    api.get("/coverage/post-arrival")
      .then((r) => setCoverage(r.data))
      .catch(() => setCoverage({ with_playbook: [] }));
  }, []);

  const filtered = useMemo(() => {
    if (!countries) return null;
    const s = q.trim().toLowerCase();
    const arr = s ? countries.filter((c) => c.country_name.toLowerCase().includes(s)) : [...countries];
    arr.sort(SORTS[sort].fn);
    return arr;
  }, [countries, q, sort]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 md:pt-20 pb-24">
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-widest text-muted font-bold">Coverage · phase 1 = Europe</div>
        <h1 className="mt-3 text-5xl md:text-6xl lg:text-7xl font-bold tracking-[-0.04em] leading-[0.95]">Nineteen countries.</h1>
        <p className="mt-5 text-[17px] text-muted">
          Tap any country for its official visa checklist, fees, funds, processing time and booking link.
        </p>
      </div>

      {/* Filter + sort. Deliberately just these two. */}
      <div className="mt-12 flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
        <div className="relative md:flex-1 md:max-w-md">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/60 pointer-events-none" />
          <input
            data-testid="countries-search"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find your country"
            className="w-full h-12 pl-11 pr-4 text-[15px] text-ink placeholder-ink/45 bg-paper border border-ink rounded-full focus:outline-none focus:ring-2 focus:ring-[color:var(--moe-elec)]/25"
          />
        </div>
        <div className="relative">
          <label className="sr-only" htmlFor="countries-sort">Sort by</label>
          <select
            id="countries-sort"
            data-testid="countries-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="h-12 pl-4 pr-10 text-[15px] font-semibold text-ink bg-paper border border-ink rounded-full appearance-none focus:outline-none focus:ring-2 focus:ring-[color:var(--moe-elec)]/25"
          >
            {Object.entries(SORTS).map(([k, s]) => (
              <option key={k} value={k}>Sort: {s.label}</option>
            ))}
          </select>
          <span aria-hidden="true" className="absolute right-4 top-1/2 -translate-y-1/2 text-ink/70 text-xs">▾</span>
        </div>
      </div>

      {!filtered ? (
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {Array.from({ length: 9 }).map((_, i) => <div key={i} className="moe-shimmer h-64" />)}
        </div>
      ) : loadError ? (
        <div className="mt-16 moe-card p-10 text-center" data-testid="countries-load-error">
          <div className="text-lg font-bold">Couldn't reach the country registry.</div>
          <div className="mt-1 text-sm text-muted">Check your connection and refresh. We cover 19 European countries and the list is normally instant.</div>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 h-10 px-5 rounded-full bg-ink text-white font-bold text-sm hover:bg-elec transition-colors"
            data-testid="countries-reload"
          >
            Refresh
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-16 moe-card p-10 text-center" data-testid="countries-empty">
          <div className="text-lg font-bold">
            {q.trim()
              ? <>No country matches &ldquo;{q}&rdquo;.</>
              : "No countries to show right now."}
          </div>
          <div className="mt-1 text-sm text-muted">Try a broader search. We cover 19 European countries.</div>
        </div>
      ) : (
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 moe-stagger" data-testid="countries-grid">
          {filtered.map((c) => (
            <CountryCard
              key={c.country_name}
              c={c}
              hasPlaybook={coverage.with_playbook.includes(c.country_name)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
